import type { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { TriangleAlert, UserPlus, Users } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAppHeader, type AppHeaderConfig } from "@/components/app-header";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { useListPeek } from "@/components/ui/peek-panel";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Tag } from "@/components/ui/tag";

import { ForeignNationalsAPI } from "../../api/foreignNationals";
import { MattersAPI } from "../../api/matters";
import { ForeignNational } from "../../api/types";
import type {
  ClientFilterCounts,
  ClientListFilter
} from "../../api/types/clientDirectory";
import { MatterDTO } from "../../api/types/matter";
import { VisaTag } from "../../components/common/VisaTag";
import {
  caseWorkspacePath,
  matterCaseRef
} from "../../features/caseIde/routes";
import { AssignedUsersAvatarStack } from "../../features/foreignNationals/components/AssignedUsersAvatarStack";
import { useForeignNationals } from "../../features/foreignNationals/context/ForeignNationalsContext";
import { openNewClient } from "../../features/newClient";
import { useUserProfiles } from "../../features/userProfiles/context/UserProfilesContext";
import { usePracticeTeamMembers } from "../../hooks/usePracticeTeamMembers";
import { useUserRoleAndSubscription } from "../../hooks/useUserRoleAndSubscription";
import { VISAVALUE } from "../../types/visa.type";
import { getWorkflowType, WorkflowType } from "../../types/workflow.type";
import { asVisaValue } from "../../utils/matterDisplay";
import {
  CLIENT_COLUMNS,
  initialsFromName,
  visibleClientColumns,
  type AssignmentFilter,
  type ClientColumnId,
  type SortFilter
} from "./clientsListFilters";
import {
  CitizenshipCell,
  DeadlineCell,
  DuplicateFlag,
  hasDeadline,
  hasStatus,
  PortalCell,
  StatusCell
} from "./components/ClientListCells";
import { ClientPeek } from "./components/ClientPeek";
import { ClientsTableToolbar } from "./components/ClientsTableToolbar";

const CLIENTS_HEADER: AppHeaderConfig = {
  crumbs: [{ id: "clients", label: "Clients", current: true, icon: Users }]
};

const getFullName = (item: ForeignNational) =>
  [item.first_name, item.middle_name, item.last_name]
    .filter((part) => typeof part === "string" && part.trim())
    .join(" ");

const getNamePart = (value: unknown): string => {
  if (typeof value !== "string") return "";
  return value.trim();
};

const compareName = (a: ForeignNational, b: ForeignNational) => {
  const aFirst = getNamePart(a.first_name);
  const bFirst = getNamePart(b.first_name);
  const firstCmp = aFirst.localeCompare(bFirst, undefined, {
    sensitivity: "base"
  });
  if (firstCmp !== 0) return firstCmp;

  const aLast = getNamePart(a.last_name);
  const bLast = getNamePart(b.last_name);
  const lastCmp = aLast.localeCompare(bLast, undefined, {
    sensitivity: "base"
  });
  if (lastCmp !== 0) return lastCmp;

  return getFullName(a).localeCompare(getFullName(b), undefined, {
    sensitivity: "base"
  });
};

const WorkflowMatterTag = ({
  visa,
  isRfe,
  workflowType,
  matter,
  size,
  onOpenMatter
}: {
  visa: string;
  isRfe?: boolean;
  workflowType?: WorkflowType | string | null;
  matter: MatterDTO | null;
  size: "xs" | "sm";
  onOpenMatter: (matter: MatterDTO) => void;
}) => {
  const isClickable = Boolean(matter);

  return (
    <button
      type="button"
      className={[
        "group inline-flex items-center",
        isClickable ? "cursor-pointer" : "cursor-default"
      ].join(" ")}
      onClick={(event) => {
        event.stopPropagation();
        if (matter) onOpenMatter(matter);
      }}
      disabled={!isClickable}
      aria-label={isClickable ? `Open ${visa} matter` : visa}
    >
      <VisaTag
        visaType={asVisaValue(visa)}
        isRfe={Boolean(isRfe)}
        workflowType={workflowType}
        size={size}
        rightIcon={isClickable ? "FeatherArrowUpRight" : undefined}
        rightIconVisibility="hover"
      />
    </button>
  );
};

const sortItems = (items: ForeignNational[], sortFilter: SortFilter) => {
  const next = [...items];

  switch (sortFilter) {
    case "oldest":
      return next.sort(
        (a, b) =>
          dayjs(a.created_at || 0).valueOf() -
          dayjs(b.created_at || 0).valueOf()
      );
    case "firstNameAsc":
      return next.sort(compareName);
    case "firstNameDesc":
      return next.sort((a, b) => compareName(b, a));
    case "lastNameAsc":
      return next.sort((a, b) => {
        const aLast = getNamePart(a.last_name);
        const bLast = getNamePart(b.last_name);
        const lastCmp = aLast.localeCompare(bLast, undefined, {
          sensitivity: "base"
        });
        if (lastCmp !== 0) return lastCmp;
        return compareName(a, b);
      });
    case "lastNameDesc":
      return next.sort((a, b) => {
        const aLast = getNamePart(a.last_name);
        const bLast = getNamePart(b.last_name);
        const lastCmp = bLast.localeCompare(aLast, undefined, {
          sensitivity: "base"
        });
        if (lastCmp !== 0) return lastCmp;
        return compareName(b, a);
      });
    case "newest":
    default:
      return next.sort(
        (a, b) =>
          dayjs(b.created_at || 0).valueOf() -
          dayjs(a.created_at || 0).valueOf()
      );
  }
};

const matchesAssignmentFilter = (
  item: ForeignNational,
  filter: AssignmentFilter
) => {
  switch (filter) {
    case "unassigned":
      return item.assigned_to_user_ids.length === 0;
    case "all":
    default:
      return true;
  }
};

const matchesAssignedUserFilter = (
  item: ForeignNational,
  assignedUserUid: string
) => {
  const needle = assignedUserUid.trim();
  if (!needle) return true;
  const ids = new Set<string>(
    (item.assigned_to_user_ids ?? []).filter(Boolean)
  );
  (item.assigned_to_user_profiles ?? []).forEach((p) => {
    const uid = (p?.uid ?? "").trim();
    if (uid) ids.add(uid);
  });
  return ids.has(needle);
};

const normalizeForeignNational = (item: ForeignNational): ForeignNational => {
  const assignedRaw = (item as unknown as { assigned_to_user_ids?: unknown })
    .assigned_to_user_ids;
  const assigned =
    Array.isArray(assignedRaw) &&
    assignedRaw.every((value) => typeof value === "string")
      ? assignedRaw
      : Array.isArray(assignedRaw)
      ? assignedRaw.filter(
          (value): value is string => typeof value === "string"
        )
      : [];

  const profilesRaw = (
    item as unknown as { assigned_to_user_profiles?: unknown }
  ).assigned_to_user_profiles;
  const assignedProfiles = Array.isArray(profilesRaw) ? profilesRaw : [];

  return {
    ...item,
    first_name: typeof item.first_name === "string" ? item.first_name : "",
    middle_name: typeof item.middle_name === "string" ? item.middle_name : "",
    last_name: typeof item.last_name === "string" ? item.last_name : "",
    email: typeof item.email === "string" ? item.email : "",
    practice_name:
      typeof item.practice_name === "string" ? item.practice_name : "",
    assigned_to_user_ids: assigned,
    assigned_to_user_profiles:
      assignedProfiles as ForeignNational["assigned_to_user_profiles"]
  };
};

type WorkflowItem = {
  visa: string;
  isRfe: boolean;
  workflowType: WorkflowType;
  matter: MatterDTO;
};

/** One tag per distinct visa and workflow across a client's matters. */
const workflowItemsOf = (matters: MatterDTO[]): WorkflowItem[] => {
  const out: WorkflowItem[] = [];
  const seen = new Set<string>();
  matters.forEach((m) => {
    const visa = (m.case?.visa ?? "").toString().trim();
    if (!visa || visa === VISAVALUE.EMPTY) return;
    const ind = m.individual as unknown as {
      is_rfe?: unknown;
      isRfe?: unknown;
      workflow_type?: string | null;
    } | null;
    const isRfe = Boolean(ind?.is_rfe ?? ind?.isRfe);
    const workflowType = getWorkflowType(ind?.workflow_type, isRfe);
    const key = `${visa}::${workflowType}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ visa, isRfe, workflowType, matter: m });
  });
  return out;
};

const WORKFLOWS_SHOWN = 3;

const ROW_TEST_ID = "client-row-";

const clientIdOf = (item: ForeignNational) => (item.uid ?? "").trim();

const clientIdFromElement = (el: Element): string | null => {
  const row = el.closest(`tr[data-testid^="${ROW_TEST_ID}"]`);
  return row?.getAttribute("data-testid")?.slice(ROW_TEST_ID.length) || null;
};

const clientRowElement = (id: string) =>
  document.querySelector<HTMLElement>(
    `tr[data-testid="${ROW_TEST_ID}${CSS.escape(id)}"]`
  );

export const ForeignNationalsDashboard = () => {
  const PAGE_LIMIT = 50;
  const TABLE_INFINITE_SCROLL_THRESHOLD_PX = 200;
  useAppHeader(CLIENTS_HEADER);
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshNonce: foreignNationalsRefreshNonce } = useForeignNationals();
  const { isSuperAdmin, lawyerPractice } = useUserRoleAndSubscription();
  const [items, setItems] = useState<ForeignNational[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [, setAssignedCount] = useState(0);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearchValue] = useState("");
  const searchTimeoutRef = useRef<number | null>(null);
  const requestSeqRef = useRef(0);
  const appendNextRef = useRef(false);
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const infiniteScrollInFlightRef = useRef(false);
  const [dashboardRefreshNonce, setDashboardRefreshNonce] = useState(0);
  const [assignmentFilter, setAssignmentFilter] =
    useState<AssignmentFilter>("all");
  const [assignedUserUidFilter, setAssignedUserUidFilter] = useState("");
  const [sortFilter, setSortFilter] = useState<SortFilter>("newest");
  const [listFilter, setListFilter] = useState<ClientListFilter | "">("");
  const [filterCounts, setFilterCounts] = useState<ClientFilterCounts | null>(
    null
  );
  const [mattersByClientId, setMattersByClientId] = useState<
    Record<string, MatterDTO[] | undefined>
  >({});
  const [columnOverrides, setColumnOverrides] = useState<
    Partial<Record<ClientColumnId, boolean>>
  >({});
  const inFlightMattersIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (assignmentFilter === "unassigned" && assignedUserUidFilter) {
      setAssignedUserUidFilter("");
    }
  }, [assignmentFilter, assignedUserUidFilter]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        window.clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const { options: practiceTeamMemberOptions } =
    usePracticeTeamMembers(lawyerPractice);
  const { ensure: ensureUserProfiles, get: getUserProfile } = useUserProfiles();

  const visibleItems = items;

  useEffect(() => {
    const ids = new Set<string>();
    visibleItems.forEach((item) => {
      const clientUid = (item?.uid ?? "").trim();
      if (clientUid) ids.add(clientUid);
      (item.assigned_to_user_ids ?? []).forEach((id) => {
        const trimmed = (id ?? "").trim();
        if (trimmed) ids.add(trimmed);
      });
    });
    practiceTeamMemberOptions.forEach((member) => {
      const trimmed = (member.uid ?? "").trim();
      if (trimmed) ids.add(trimmed);
    });
    if (ids.size === 0) return;
    ensureUserProfiles(Array.from(ids));
  }, [ensureUserProfiles, practiceTeamMemberOptions, visibleItems]);

  const fetchList = useCallback(async () => {
    requestSeqRef.current += 1;
    const seq = requestSeqRef.current;
    const shouldAppend = appendNextRef.current && offset > 0;
    appendNextRef.current = false;

    if (shouldAppend) setIsLoadingMore(true);
    else setIsLoadingList(true);
    setError(null);

    try {
      const res = await ForeignNationalsAPI.list({
        limit: PAGE_LIMIT,
        offset,
        q: search.trim() ? search.trim() : undefined,
        include_all_practices: isSuperAdmin ? true : undefined,
        assignment_filter: assignmentFilter,
        assigned_user_uid: assignedUserUidFilter.trim()
          ? assignedUserUidFilter.trim()
          : undefined,
        sort: sortFilter,
        filter: listFilter || undefined,
        include_meta: true
      });
      if (requestSeqRef.current !== seq) return;
      if (res.filter_counts) setFilterCounts(res.filter_counts);

      const page = (res.foreign_nationals ?? []).map(normalizeForeignNational);
      setItems((prev) => (shouldAppend ? [...prev, ...page] : page));
      setTotalCount(res.total_count ?? 0);
      setAssignedCount(res.assigned_count ?? 0);
      if (page.length === 0) {
        setHasMore(false);
      } else {
        const nextLoaded = offset + page.length;
        const total = res.total_count ?? 0;
        setHasMore(total > 0 ? nextLoaded < total : page.length >= PAGE_LIMIT);
      }
    } catch (err) {
      if (requestSeqRef.current !== seq) return;
      const e =
        err instanceof Error ? err : new Error("Failed to load clients");
      setError(e);
      if (!shouldAppend) {
        setItems([]);
        setTotalCount(0);
        setAssignedCount(0);
      }
    } finally {
      if (requestSeqRef.current === seq) {
        if (shouldAppend) setIsLoadingMore(false);
        else setIsLoadingList(false);
        infiniteScrollInFlightRef.current = false;
      }
    }
  }, [
    PAGE_LIMIT,
    assignmentFilter,
    assignedUserUidFilter,
    isSuperAdmin,
    listFilter,
    offset,
    search,
    sortFilter
  ]);

  const resetToFirstPage = useCallback(() => {
    appendNextRef.current = false;
    infiniteScrollInFlightRef.current = false;
    setOffset(0);
    setHasMore(true);
    setItems([]);
    setMattersByClientId({});
  }, []);

  const refresh = useCallback(() => {
    resetToFirstPage();
    setDashboardRefreshNonce((n) => n + 1);
  }, [resetToFirstPage]);

  useEffect(() => {
    const state = location.state as
      | { refreshClientsList?: boolean; createdClientUid?: string }
      | null
      | undefined;

    if (!state?.refreshClientsList) return;

    refresh();
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate, refresh]);
  const lastForeignNationalsRefreshNonceRef = useRef(
    foreignNationalsRefreshNonce
  );

  useEffect(() => {
    if (
      lastForeignNationalsRefreshNonceRef.current ===
      foreignNationalsRefreshNonce
    ) {
      return;
    }
    lastForeignNationalsRefreshNonceRef.current = foreignNationalsRefreshNonce;
    refresh();
  }, [foreignNationalsRefreshNonce, refresh]);

  useEffect(() => {
    fetchList();
  }, [fetchList, dashboardRefreshNonce]);

  const setSearch = useCallback(
    (value: string) => {
      setSearchInput(value);
      resetToFirstPage();
      if (searchTimeoutRef.current) {
        window.clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = window.setTimeout(() => {
        setSearchValue(value);
      }, 300);
    },
    [resetToFirstPage]
  );

  const handleAssignmentFilterChange = useCallback(
    (value: AssignmentFilter) => {
      setAssignmentFilter(value);
      resetToFirstPage();
    },
    [resetToFirstPage]
  );

  const handleAssignedUserUidFilterChange = useCallback(
    (value: string) => {
      setAssignedUserUidFilter(value);
      resetToFirstPage();
    },
    [resetToFirstPage]
  );

  const handleSortFilterChange = useCallback(
    (value: SortFilter) => {
      setSortFilter(value);
      resetToFirstPage();
    },
    [resetToFirstPage]
  );

  const handleListFilterChange = useCallback(
    (value: ClientListFilter | "") => {
      setListFilter(value);
      resetToFirstPage();
    },
    [resetToFirstPage]
  );

  const loadMore = useCallback(() => {
    if (error) return;
    if (!hasMore) return;
    if (isLoadingMore) return;
    if (infiniteScrollInFlightRef.current) return;
    infiniteScrollInFlightRef.current = true;
    appendNextRef.current = true;
    setOffset(items.length);
  }, [error, hasMore, isLoadingMore, items.length]);

  const maybeLoadMore = useCallback(() => {
    const el = tableScrollContainerRef.current;
    if (!el) return;
    if (error) return;
    if (!hasMore) return;
    if (isLoadingMore) return;
    if (isLoadingList) return;
    if (infiniteScrollInFlightRef.current) return;

    const distanceFromBottom =
      el.scrollHeight - (el.scrollTop + el.clientHeight);
    if (distanceFromBottom > TABLE_INFINITE_SCROLL_THRESHOLD_PX) return;

    loadMore();
  }, [
    TABLE_INFINITE_SCROLL_THRESHOLD_PX,
    error,
    hasMore,
    isLoadingList,
    isLoadingMore,
    loadMore
  ]);

  useEffect(() => {
    if (isLoadingList) return;
    maybeLoadMore();
  }, [isLoadingList, items.length, maybeLoadMore]);

  useEffect(() => {
    if (isLoadingList || isLoadingMore) return;
    if (error) return;

    const idsToFetch = visibleItems
      .map((item) => (item?.uid ?? "").trim())
      .filter((uid) => {
        if (!uid) return false;
        if (mattersByClientId[uid] !== undefined) return false; // already loaded
        if (inFlightMattersIdsRef.current.has(uid)) return false; // already requesting
        return true;
      });

    if (idsToFetch.length === 0) return;

    idsToFetch.forEach((uid) => inFlightMattersIdsRef.current.add(uid));

    const fetchOne = async (uid: string) => {
      try {
        const response = await MattersAPI.listForForeignNational(uid);
        const matters = Array.isArray(response) ? response : [];
        setMattersByClientId((prev) => ({ ...prev, [uid]: matters }));
      } catch {
        // Treat errors as "no matters" so the UI doesn't get stuck.
        setMattersByClientId((prev) => ({ ...prev, [uid]: [] }));
      } finally {
        inFlightMattersIdsRef.current.delete(uid);
      }
    };

    const run = async () => {
      const concurrency = 4;
      for (let i = 0; i < idsToFetch.length; i += concurrency) {
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(idsToFetch.slice(i, i + concurrency).map(fetchOne));
      }
    };

    run();
  }, [visibleItems, isLoadingList, isLoadingMore, error, mattersByClientId]);

  const isTrulyEmpty =
    !searchInput.trim() &&
    !listFilter &&
    assignmentFilter === "all" &&
    !assignedUserUidFilter.trim() &&
    totalCount === 0;

  const clearFilters = () => {
    setSearchInput("");
    setSearchValue("");
    setAssignmentFilter("all");
    setAssignedUserUidFilter("");
    setSortFilter("newest");
    setListFilter("");
    refresh();
  };

  const anyStatus = visibleItems.some((item) => hasStatus(item.list_meta));
  const anyDeadline = visibleItems.some((item) => hasDeadline(item.list_meta));
  const visibleColumns = useMemo(
    () =>
      visibleClientColumns({
        overrides: columnOverrides,
        isSuperAdmin,
        anyStatus,
        anyDeadline
      }),
    [anyDeadline, anyStatus, columnOverrides, isSuperAdmin]
  );
  const columnOptions = useMemo(
    () =>
      CLIENT_COLUMNS.filter((c) => c.id !== "firm" || isSuperAdmin).map(
        (c) => ({ ...c, visible: visibleColumns.has(c.id) })
      ),
    [isSuperAdmin, visibleColumns]
  );
  const columnsNote =
    !anyStatus || !anyDeadline
      ? "Status and Next Deadline stay hidden until a client has one."
      : undefined;

  const openClient = useCallback(
    (item: ForeignNational) => navigate(`/clients/${clientIdOf(item)}`),
    [navigate]
  );
  const rowIds = useMemo(() => visibleItems.map(clientIdOf), [visibleItems]);
  const peek = useListPeek({
    ids: rowIds,
    onOpen: (id) => navigate(`/clients/${id}`),
    onFocusRow: (id) =>
      clientRowElement(id)?.scrollIntoView?.({ block: "nearest" }),
    enabled: !error
  });
  const peekClient = peek.peekId
    ? visibleItems.find((item) => clientIdOf(item) === peek.peekId) ?? null
    : null;

  const columns = useMemo<ColumnDef<ForeignNational, unknown>[]>(() => {
    const openMatter = (m: MatterDTO) =>
      navigate(caseWorkspacePath(matterCaseRef(m)));
    const cols: ColumnDef<ForeignNational, unknown>[] = [
      {
        id: "client",
        header: "Client",
        cell: ({ row }) => {
          const item = row.original;
          const clientId = (item.uid ?? "").trim();
          const fullName = getFullName(item) || "Unknown";
          const avatar = getUserProfile(clientId)?.avatar_data_url;
          return (
            <div className="flex items-center gap-3">
              <Avatar size="md" shape="square">
                {avatar ? <AvatarImage src={avatar} alt="" /> : null}
                <AvatarFallback>{initialsFromName(fullName)}</AvatarFallback>
              </Avatar>
              <span className="flex min-w-0 items-center gap-2">
                <span className="truncate text-fg">{fullName}</span>
                <DuplicateFlag
                  meta={item.list_meta}
                  onReview={(otherId) =>
                    navigate(
                      `/clients/${clientId}?tab=settings&merge=${otherId}`
                    )
                  }
                />
              </span>
            </div>
          );
        }
      }
    ];
    cols.push(
      {
        id: "firm",
        header: "Law Firm",
        cell: ({ row }) => (
          <span className="text-fg-3">{row.original.practice_name || "—"}</span>
        )
      },
      {
        id: "citizenship",
        header: "Citizenship",
        cell: ({ row }) => <CitizenshipCell meta={row.original.list_meta} />
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => <StatusCell meta={row.original.list_meta} />
      },
      {
        id: "deadline",
        header: "Next Deadline",
        cell: ({ row }) => (
          <DeadlineCell
            meta={row.original.list_meta}
            matters={mattersByClientId[(row.original.uid ?? "").trim()]}
          />
        )
      },
      {
        id: "workflows",
        header: "Workflows",
        meta: { className: "min-w-35 sm:min-w-60" },
        cell: ({ row }) => {
          const clientId = (row.original.uid ?? "").trim();
          const matters = mattersByClientId[clientId];
          if (matters === undefined) {
            return (
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="hidden h-5 w-16 rounded-full sm:block" />
              </div>
            );
          }
          const workflows = workflowItemsOf(matters);
          if (!workflows.length) return <span className="text-fg-4">—</span>;
          const shown = workflows.slice(0, WORKFLOWS_SHOWN);
          const overflow = workflows.length - shown.length;
          return (
            <div className="flex max-w-80 flex-wrap items-center gap-1.5">
              {shown.map((wf) => (
                <WorkflowMatterTag
                  key={`${clientId}-${wf.visa}-${wf.workflowType}`}
                  visa={wf.visa}
                  isRfe={wf.isRfe}
                  workflowType={wf.workflowType}
                  matter={wf.matter}
                  size="sm"
                  onOpenMatter={openMatter}
                />
              ))}
              {overflow > 0 ? <Tag>+{overflow}</Tag> : null}
            </div>
          );
        }
      },
      {
        id: "portal",
        header: "Portal",
        cell: ({ row }) => <PortalCell meta={row.original.list_meta} />
      },
      {
        id: "assignment",
        header: "Team",
        cell: ({ row }) => {
          const ids = row.original.assigned_to_user_ids ?? [];
          const profiles = row.original.assigned_to_user_profiles ?? [];
          const uids = ids.length
            ? ids
            : profiles.map((p) => (p?.uid ?? "").trim()).filter(Boolean);
          return uids.length ? (
            <AssignedUsersAvatarStack userIds={uids} />
          ) : (
            <span className="text-fg-4">Unassigned</span>
          );
        }
      }
    );
    return cols.filter(
      (c) => c.id === "client" || visibleColumns.has(c.id as ClientColumnId)
    );
  }, [getUserProfile, mattersByClientId, navigate, visibleColumns]);

  return (
    <div className="flex h-full min-h-0 w-full bg-surface">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex shrink-0 flex-col gap-4 px-6 pt-6 pb-3">
          <PageHeader
            title="Clients"
            count={isLoadingList && totalCount === 0 ? null : totalCount}
            actions={
              <Button variant="primary" size="sm" onClick={openNewClient}>
                <UserPlus />
                New Client
              </Button>
            }
          />
          <ClientsTableToolbar
            searchInput={searchInput}
            onSearchChange={setSearch}
            sortFilter={sortFilter}
            onSortChange={handleSortFilterChange}
            assignmentFilter={assignmentFilter}
            onAssignmentFilterChange={handleAssignmentFilterChange}
            assignedUserUidFilter={assignedUserUidFilter}
            onAssignedUserUidFilterChange={handleAssignedUserUidFilterChange}
            practiceTeamMemberOptions={practiceTeamMemberOptions}
            quickFilters={{
              value: listFilter,
              counts: filterCounts,
              onChange: handleListFilterChange
            }}
            columns={columnOptions}
            onColumnChange={(id, visible) =>
              setColumnOverrides((current) => ({ ...current, [id]: visible }))
            }
            columnsNote={columnsNote}
            onRefresh={refresh}
          />
        </div>

        <div
          ref={tableScrollContainerRef}
          className="flex min-h-0 w-full flex-1 flex-col overflow-auto"
          onScroll={maybeLoadMore}
          onMouseOver={(e) =>
            peek.setHoverId(
              e.target instanceof Element ? clientIdFromElement(e.target) : null
            )
          }
          onFocus={(e) => peek.setHoverId(clientIdFromElement(e.target))}
          onMouseLeave={() => peek.setHoverId(null)}
        >
          {error ? (
            <EmptyState
              icon={TriangleAlert}
              title="Couldn't Load Clients"
              description="Something went wrong loading the client list."
              action={
                <Button variant="secondary" size="sm" onClick={refresh}>
                  Retry
                </Button>
              }
              className="flex-1"
            />
          ) : (
            <>
              <DataTable
                data={visibleItems}
                columns={columns}
                rowHeight="double"
                getRowId={clientIdOf}
                getRowTestId={(item) => `${ROW_TEST_ID}${clientIdOf(item)}`}
                activeRowId={peek.peekId ?? peek.activeId}
                onRowClick={openClient}
                isLoading={isLoadingList}
                className="overflow-visible"
                empty={
                  <EmptyState
                    icon={Users}
                    title={
                      isTrulyEmpty ? "No Clients Yet" : "No Matching Clients"
                    }
                    description={
                      isTrulyEmpty
                        ? "Create your first client to start managing profiles here."
                        : "Try a different search or clear the filters."
                    }
                    action={
                      isTrulyEmpty ? (
                        <Button size="sm" onClick={openNewClient}>
                          Create Client
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={clearFilters}
                        >
                          Clear Search and Filters
                        </Button>
                      )
                    }
                    className="py-16"
                  />
                }
              />
              {isLoadingMore ? (
                <div className="flex w-full justify-center py-4">
                  <Spinner className="size-4 text-fg-3" />
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
      {peekClient ? (
        <ClientPeek
          client={peekClient}
          fullName={getFullName(peekClient) || "Unknown"}
          avatar={getUserProfile(clientIdOf(peekClient))?.avatar_data_url}
          matters={mattersByClientId[clientIdOf(peekClient)]}
          getUserProfile={getUserProfile}
          onOpen={() => openClient(peekClient)}
          onOpenMatter={(m) => navigate(caseWorkspacePath(matterCaseRef(m)))}
          onOpenPortal={() =>
            navigate(`/clients/${clientIdOf(peekClient)}?tab=overview&portal=1`)
          }
          onReviewDuplicate={(otherId) =>
            navigate(
              `/clients/${clientIdOf(peekClient)}?tab=settings&merge=${otherId}`
            )
          }
        />
      ) : null}
    </div>
  );
};
